import React from 'react';
import { jsPDF } from 'jspdf';
import {
  Box,
  Typography,
  Grid,
  Divider,
  Button,
  Alert,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { usePortalProfile } from '../../contexts/PortalProfileContext';
import { get, post } from '../../utils/api';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import {
  portalTableContainerSx,
  portalTableHeadCellSx,
  portalTableBodyRowSx,
} from '../../components/portal/portalUi';

const STATEMENT_EMAIL_STYLE = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #222; }
        .muted { color: #666; }
        .header-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        .header-table td { vertical-align: top; width: 50%; padding: 4px 6px; }
        .greeting { margin: 10px 0; }
        .table { width: 100%; border-collapse: collapse; }
        .table th, .table td { padding: 6px 8px; border-bottom: 1px solid #e0e0e0; text-align: left; }
        .table th.right, .table td.right { text-align: right; }
        .title { margin: 10px 0 6px 0; font-weight: bold; }
        .total { text-align: right; font-weight: bold; margin-top: 8px; }
      </style>`;

function sanitizeStatementFileNameSegment(raw) {
  return String(raw || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'Devotee';
}

function variationLabelFromFacetRow(variation) {
  if (!variation) return '';
  const attrs = Array.isArray(variation.attributes) ? variation.attributes : [];
  const fromAttrs = attrs
    .map((a) => {
      const attrName = String(a.name || a.attribute || '').trim();
      const value = String(a.option || a.value || '').trim();
      return attrName && value ? `${attrName}: ${value}` : (value || attrName);
    })
    .filter(Boolean)
    .join(', ');
  return String(variation.display_name || '').trim() || fromAttrs || String(variation.sku || '').trim();
}

function buildStatementItemLabel(li) {
  const rawName = String(li?.product_name || li?.name || '').trim();
  if (!rawName) return '';
  let lineName = rawName.replace(/^Select:\s*/i, '');
  let variation = String(li?.variation_label || li?.variation_name || li?.variation || '').trim();
  variation = variation.replace(/^Select:\s*/i, '');
  if (variation && lineName.includes(variation)) {
    variation = '';
  }
  const qty = Number(li?.quantity || 0);
  const qtyLabel = qty > 1 ? ` x${qty}` : '';
  return variation ? `${lineName} - ${variation}${qtyLabel}` : `${lineName}${qtyLabel}`;
}

export default function PortalStatementsPage() {
  const { profile } = usePortalProfile();
  const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Devotee Name';
  const email = profile.email || '';
  const phone = profile.phone || '';
  const address1 = profile.billing?.address_1 || '';
  const address2 = profile.billing?.address_2 || '';
  const city = profile.billing?.city || '';
  const state = profile.billing?.state || '';
  const zip = profile.billing?.postcode || '';
  const country = profile.billing?.country || '';
  const addrLine1 = [address1, address2].filter(Boolean).join(', ');
  const addrLine2 = `${city}${city && state ? ', ' : ''}${state} ${zip}`.trim();
  const fullAddress = [addrLine1, addrLine2, country].filter(Boolean).join('\n');

  const [stmtYear, setStmtYear] = React.useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = React.useState([]);
  const [yearsLoading, setYearsLoading] = React.useState(true);
  const [yearsError, setYearsError] = React.useState('');
  const [stmtLoading, setStmtLoading] = React.useState(false);
  const [stmtError, setStmtError] = React.useState('');
  const [stmtOrders, setStmtOrders] = React.useState([]);
  const [stmtEmailSending, setStmtEmailSending] = React.useState(false);
  const [stmtEmailStatus, setStmtEmailStatus] = React.useState({ type: '', message: '' });
  const [stmtPdfGenerating, setStmtPdfGenerating] = React.useState(false);
  const [stmtPdfError, setStmtPdfError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setYearsLoading(true);
      setYearsError('');
      try {
        const res = await get('/portal/orders/contribution-years');
        const ys = Array.isArray(res?.years)
          ? [...res.years].filter((y) => Number.isInteger(y)).sort((a, b) => b - a)
          : [];
        if (cancelled) return;
        setAvailableYears(ys);
        setStmtYear((prev) => {
          if (ys.length === 0) return new Date().getFullYear();
          return ys.includes(prev) ? prev : ys[0];
        });
      } catch (e) {
        if (!cancelled) {
          setYearsError(e?.message || 'Could not load contribution years');
          setAvailableYears([]);
        }
      } finally {
        if (!cancelled) setYearsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadYearOrders = React.useCallback(async (year) => {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    const per_page = 100;
    let page = 1;
    let all = [];
    while (true) {
      const rangeParams = {
        start: `${start}T00:00:00.000Z`,
        end: `${end}T23:59:59.999Z`,
        status: 'completed',
        page,
        per_page,
      };
      const resp = await get('/portal/orders', rangeParams);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      const detailed = await Promise.all(batch.map(async (o) => {
        try {
          const det = await get(`/portal/orders/${o.id}`);
          const line_items = Array.isArray(det?.items) ? det.items.map((it) => ({
            id: it.item_id || it.id,
            name: it.name,
            product_id: it.product_id,
            variation_id: it.variation_id,
            quantity: it.quantity,
            sku: it.sku,
            total: String(it.total_num ?? it.price_num ?? 0),
          })) : [];
          const fee_lines = Array.isArray(det?.fee_lines) ? det.fee_lines : [];
          return {
            id: o.id,
            status: o.status,
            currency: o.currency || 'USD',
            total: String(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            line_items,
            fee_lines,
          };
        } catch {
          return {
            id: o.id,
            status: o.status,
            currency: o.currency || 'USD',
            total: String(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            line_items: [],
            fee_lines: [],
          };
        }
      }));
      try {
        const productIds = Array.from(new Set(
          detailed.flatMap((ord) => (ord.line_items || []).map((li) => li.product_id)).filter(Boolean)
        ));
        let variationLabelById = new Map();
        let productNameById = new Map();
        if (productIds.length > 0) {
          const resVars = await get('/portal/facets/variations', { product_id: productIds.join(',') });
          const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
          variationLabelById = new Map(vars.map((v) => [String(v.id), variationLabelFromFacetRow(v)]));
          const productRows = await Promise.all(productIds.map((pid) =>
            get('/portal/facets/products', { product_id: String(pid) }).catch(() => null)
          ));
          productNameById = new Map(productRows.flatMap((res) => {
            const row = Array.isArray(res?.products) ? res.products[0] : null;
            return row?.id && row?.name ? [[String(row.id), row.name]] : [];
          }));
        }
        for (const ord of detailed) {
          if (Array.isArray(ord.line_items)) {
            ord.line_items = ord.line_items.map((li) => ({
              ...li,
              product_name: productNameById.get(String(li.product_id)) || '',
              variation_label: li.variation_id ? variationLabelById.get(String(li.variation_id)) || '' : '',
            }));
          }
        }
      } catch {
        /* Statement can still render using order item names if facet enrichment is unavailable. */
      }
      all = all.concat(detailed);
      if (batch.length < per_page) break;
      page += 1;
    }
    return all;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setStmtLoading(true);
      setStmtError('');
      setStmtOrders([]);
      try {
        const all = await loadYearOrders(stmtYear);
        if (!cancelled) setStmtOrders(all);
      } catch (err) {
        if (!cancelled) setStmtError(err?.message || 'Failed to load statement');
      } finally {
        if (!cancelled) setStmtLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stmtYear, loadYearOrders]);

  const buildStatementBodyHtml = React.useCallback(() => {
    const year = stmtYear;
    const rows = [...stmtOrders].sort((a, b) => new Date(b.date_created || b.date_created_gmt) - new Date(a.date_created || a.date_created_gmt));
    const currency = rows[0]?.currency || 'USD';
    const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n || 0));
    const total = rows.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);

    const headerHtml = `
      <table class="header-table">
        <tr>
          <td>
            <div class="muted">For Year ${year}</div>
            <div><strong>Name:</strong> ${name}</div>
            <div><strong>Email:</strong> ${email}</div>
            <div><strong>Phone:</strong> ${phone}</div>
            ${fullAddress ? `<div><strong>Address:</strong> ${fullAddress.split('\n').join(', ')}</div>` : ''}
          </td>
          <td>
            <div class="muted">Statement Provided By</div>
            <div><strong>India Cultural Center and Temple (ICCT)</strong></div>
            <div>12005 Highway 64, Eads, TN 38028</div>
            <div>Phone: (901) 202-6070  ·  Email: icct@icctmemphis.org</div>
            <div>Website: www.icctmemphis.org  ·  Tax ID: 58-1522313</div>
          </td>
        </tr>
      </table>`;

    const greetingHtml = `
      <div class="greeting">
        <div>Namaste ${name},</div>
        <div class="muted">Thank you for your generous support. This is your contribution statement for the year ${year}. You may use this statement for tax purposes as permitted by IRS guidelines. ICCT provides this statement as a courtesy and is not responsible for any IRS inquiries related to misuse or interpretation of this information.</div>
        <div class="muted" style="margin-top:6px;">With gratitude,<br/>India Cultural Center and Temple (ICCT)</div>
      </div>`;

    const tableHeader = `
      <div class="title">Contributions in ${year}</div>
      <table class="table">
        <thead>
          <tr>
            <th style="width:10%">Seq #</th>
            <th style="width:20%">Date</th>
            <th style="width:20%">Status</th>
            <th>Contribution</th>
            <th class="right" style="width:15%">Total</th>
          </tr>
        </thead>
        <tbody>`;

    const tableRows = rows.map((o, idx) => {
      const date = new Date(o.date_created || o.date_created_gmt).toLocaleDateString();
      const status = String(o.status || '').charAt(0).toUpperCase() + String(o.status || '').slice(1);
      const items = Array.isArray(o.line_items) ? o.line_items : [];
      const fees = Array.isArray(o.fee_lines) ? o.fee_lines : [];
      const contributionLines = [
        ...items.map(buildStatementItemLabel).filter(Boolean),
        ...fees.map((f) => f.name || '').filter(Boolean),
      ];
      const contribHtml = contributionLines.length > 0
        ? contributionLines.map((l) => `<div>${l}</div>`).join('')
        : '—';
      return `<tr>
        <td>${idx + 1}</td>
        <td>${date}</td>
        <td>${status}</td>
        <td>${contribHtml}</td>
        <td class="right">${fmt(o.total)}</td>
      </tr>`;
    }).join('');

    const tableFooter = `</tbody></table>
      <div class="total">Total: ${fmt(total)}</div>`;

    return `
      <h2 style="margin:0 0 6px 0;">Contribution Statement</h2>
      ${headerHtml}
      ${greetingHtml}
      ${tableHeader}${tableRows}${tableFooter}`;
  }, [stmtOrders, stmtYear, name, email, phone, fullAddress]);

  const buildEmailStatementHtml = React.useCallback(
    () => `<!DOCTYPE html><html><head><meta charset="utf-8"/>${STATEMENT_EMAIL_STYLE}</head><body>${buildStatementBodyHtml()}</body></html>`,
    [buildStatementBodyHtml],
  );

  const handleDownloadStatementPdf = React.useCallback(async () => {
    setStmtPdfError('');
    if (stmtOrders.length === 0) return;
    try {
      setStmtPdfGenerating(true);
      const safeName = sanitizeStatementFileNameSegment(name);
      const filename = `ICCT Donations statement for ${safeName} ${stmtYear}.pdf`;
      const rows = [...stmtOrders].sort((a, b) => new Date(b.date_created || b.date_created_gmt) - new Date(a.date_created || a.date_created_gmt));
      const currency = rows[0]?.currency || 'USD';
      const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n || 0));
      const total = rows.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);

      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      const footerY = pageHeight - 10;
      let y = margin;

      const addFooter = () => {
        doc.setFontSize(8);
        doc.setTextColor(110);
        doc.text('India Cultural Center and Temple (ICCT)', margin, footerY);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - margin, footerY, { align: 'right' });
        doc.setTextColor(0);
      };
      const ensureSpace = (height) => {
        if (y + height <= footerY - 4) return;
        addFooter();
        doc.addPage();
        y = margin;
      };
      const textLines = (text, width) => doc.splitTextToSize(String(text || ''), width);
      const printWrapped = (text, x, width, lineHeight = 4.5) => {
        const lines = textLines(text, width);
        doc.text(lines, x, y);
        y += Math.max(lineHeight, lines.length * lineHeight);
      };

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Contribution Statement', margin, y);
      y += 8;

      doc.setFontSize(9);
      const leftX = margin;
      const rightX = margin + contentWidth / 2 + 4;
      const colWidth = contentWidth / 2 - 4;
      const headerY = y;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`For Year ${stmtYear}`, leftX, y);
      doc.text('Statement Provided By', rightX, y);
      doc.setTextColor(0);
      y += 5;
      doc.setFont('helvetica', 'bold');
      doc.text(`Name: ${name}`, leftX, y);
      doc.text('India Cultural Center and Temple (ICCT)', rightX, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(`Email: ${email}`, leftX, y);
      doc.text('12005 Highway 64, Eads, TN 38028', rightX, y);
      y += 5;
      doc.text(`Phone: ${phone}`, leftX, y);
      doc.text('Phone: (901) 202-6070', rightX, y);
      y += 5;
      const addressLines = fullAddress ? textLines(`Address: ${fullAddress.split('\n').join(', ')}`, colWidth) : [];
      if (addressLines.length > 0) {
        doc.text(addressLines, leftX, y);
      }
      doc.text('Email: icct@icctmemphis.org', rightX, y);
      y += Math.max(5, addressLines.length * 4.5);
      doc.text('Website: www.icctmemphis.org', rightX, Math.max(headerY + 25, y));
      doc.text('Tax ID: 58-1522313', rightX, Math.max(headerY + 30, y + 5));
      y = Math.max(y, headerY + 36);

      ensureSpace(28);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      printWrapped(`Namaste ${name},`, margin, contentWidth);
      printWrapped(`Thank you for your generous support. This is your contribution statement for the year ${stmtYear}. You may use this statement for tax purposes as permitted by IRS guidelines. ICCT provides this statement as a courtesy and is not responsible for any IRS inquiries related to misuse or interpretation of this information.`, margin, contentWidth, 4.2);
      printWrapped('With gratitude,\nIndia Cultural Center and Temple (ICCT)', margin, contentWidth, 4.2);
      y += 2;

      ensureSpace(12);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`Contributions in ${stmtYear}`, margin, y);
      y += 6;

      const drawTableHeader = () => {
        ensureSpace(9);
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y - 4, contentWidth, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.text('Seq #', margin + 2, y);
        doc.text('Date', margin + 22, y);
        doc.text('Status', margin + 52, y);
        doc.text('Contribution', margin + 82, y);
        doc.text('Total', pageWidth - margin - 2, y, { align: 'right' });
        y += 6;
        doc.setDrawColor(210);
        doc.line(margin, y - 2, pageWidth - margin, y - 2);
      };

      drawTableHeader();
      doc.setFont('helvetica', 'normal');
      rows.forEach((o, idx) => {
        const date = new Date(o.date_created || o.date_created_gmt).toLocaleDateString();
        const status = String(o.status || '').charAt(0).toUpperCase() + String(o.status || '').slice(1);
        const items = Array.isArray(o.line_items) ? o.line_items : [];
        const fees = Array.isArray(o.fee_lines) ? o.fee_lines : [];
        const contributionLines = [
          ...items.map(buildStatementItemLabel).filter(Boolean),
          ...fees.map((f) => f.name || '').filter(Boolean),
        ];
        const contributionText = contributionLines.length > 0 ? contributionLines.join('\n') : '-';
        const contributionWrapped = textLines(contributionText, 70);
        const rowHeight = Math.max(8, contributionWrapped.length * 4.2 + 3);
        if (y + rowHeight > footerY - 4) {
          addFooter();
          doc.addPage();
          y = margin;
          drawTableHeader();
          doc.setFont('helvetica', 'normal');
        }
        const rowTop = y - 3;
        doc.setFontSize(8.5);
        doc.text(String(idx + 1), margin + 2, y);
        doc.text(date, margin + 22, y);
        doc.text(status, margin + 52, y);
        doc.text(contributionWrapped, margin + 82, y);
        doc.text(fmt(o.total), pageWidth - margin - 2, y, { align: 'right' });
        y = rowTop + rowHeight;
        doc.setDrawColor(225);
        doc.line(margin, y, pageWidth - margin, y);
        y += 3;
      });

      ensureSpace(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`Total: ${fmt(total)}`, pageWidth - margin, y, { align: 'right' });
      addFooter();
      doc.save(filename);
    } catch (e) {
      setStmtPdfError(e?.message || 'Could not generate PDF.');
    } finally {
      setStmtPdfGenerating(false);
    }
  }, [stmtOrders, stmtYear, name, email, phone, fullAddress]);

  return (
    <>
      <PortalPageHeader title="Statements" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a year that has completed contributions. Email includes the full statement layout.
      </Typography>

      {yearsError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>{yearsError}</Typography>
      )}
      {!yearsLoading && availableYears.length === 0 && !yearsError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No completed contributions are on file yet. When you have completed donations, their years will appear here.
        </Typography>
      )}

      {stmtError && (<Typography variant="body2" color="error" sx={{ mb: 1 }}>{stmtError}</Typography>)}
      {stmtEmailStatus.message && (
        <Alert severity={stmtEmailStatus.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
          {stmtEmailStatus.message}
        </Alert>
      )}
      {stmtPdfError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setStmtPdfError('')}>
          {stmtPdfError}
        </Alert>
      )}

      {!stmtError && (
        <Box id="stmt-printable">
          <Box
            sx={{
              display: 'none',
              '@media print': { display: 'block' },
              mb: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Namaste {name},
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Thank you for your generous support. This is your contribution statement for the year {stmtYear}. You may use this statement for tax purposes as permitted by IRS guidelines. ICCT provides this statement as a courtesy and is not responsible for any IRS inquiries related to misuse or interpretation of this information.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              With gratitude,
              <br />
              India Cultural Center and Temple (ICCT)
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 2,
              '@media print': { display: 'none' },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              {yearsLoading ? (
                <Typography variant="body2" color="text.secondary">Loading years…</Typography>
              ) : availableYears.length > 0 ? (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="stmt-year-label">Statement year</InputLabel>
                  <Select
                    labelId="stmt-year-label"
                    label="Statement year"
                    value={availableYears.includes(stmtYear) ? stmtYear : availableYears[0]}
                    onChange={(e) => {
                      setStmtYear(Number(e.target.value));
                      setStmtEmailStatus({ type: '', message: '' });
                      setStmtPdfError('');
                    }}
                  >
                    {availableYears.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2" color="text.secondary">No years with completed contributions.</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={
                  stmtPdfGenerating
                  || stmtLoading
                  || yearsLoading
                  || stmtOrders.length === 0
                }
                onClick={handleDownloadStatementPdf}
              >
                {stmtPdfGenerating ? 'Preparing PDF…' : 'Download PDF'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={stmtEmailSending || stmtLoading || yearsLoading}
                onClick={async () => {
                  try {
                    if (!email) {
                      setStmtEmailStatus({ type: 'error', message: 'Devotee email is missing. Unable to send statement.' });
                      return;
                    }
                    setStmtEmailSending(true);
                    setStmtEmailStatus({ type: '', message: '' });
                    const subject = `Contribution Statement - ${stmtYear}`;
                    const html = buildEmailStatementHtml();
                    await post('/api/statements/email', { to: email, subject, html });
                    setStmtEmailStatus({
                      type: 'success',
                      message: `Contribution statement email has been sent to ${email}.`,
                    });
                  } catch (e) {
                    setStmtEmailStatus({
                      type: 'error',
                      message: `Failed to email statement: ${e?.response?.data?.error || e?.message || 'Unknown error'}`,
                    });
                  } finally {
                    setStmtEmailSending(false);
                  }
                }}
              >
                {stmtEmailSending ? 'Sending...' : 'Email contribution statement'}
              </Button>
            </Stack>
          </Box>

          {stmtLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Loading statement…</Typography>
          )}
          {!stmtLoading && stmtOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No transactions found for {stmtYear}.</Typography>
          ) : !stmtLoading ? (
            (() => {
              const rows = [...stmtOrders].sort((a, b) => new Date(b.date_created || b.date_created_gmt) - new Date(a.date_created || a.date_created_gmt));
              const total = rows.reduce((s, o) => s + (parseFloat(o.total) || 0), 0);
              const currency = rows[0]?.currency || 'USD';
              const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n || 0));
              return (
                <>
                  <Box
                    sx={{
                      display: 'none',
                      '@media print': { display: 'block' },
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>Contribution Statement</Typography>
                    <Grid container spacing={0} sx={{ columnGap: '50px' }}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>For Year {stmtYear}</Typography>
                        <Typography variant="body2"><strong>Name:</strong> {name}</Typography>
                        <Typography variant="body2"><strong>Email:</strong> {email}</Typography>
                        <Typography variant="body2"><strong>Phone:</strong> {phone}</Typography>
                        {fullAddress && (
                          <Typography variant="body2"><strong>Address:</strong> {fullAddress.split('\n').join(', ')}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Statement Provided By</Typography>
                        <Typography variant="body2"><strong>India Cultural Center and Temple (ICCT)</strong></Typography>
                        <Typography variant="body2">12005 Highway 64, Eads, TN 38028</Typography>
                        <Typography variant="body2">Phone: (901) 202-6070  ·  Email: icct@icctmemphis.org</Typography>
                        <Typography variant="body2">Website: www.icctmemphis.org  ·  Tax ID: 58-1522313</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Contributions in {stmtYear}</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={portalTableContainerSx}>
                    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={portalTableHeadCellSx}>Seq #</TableCell>
                          <TableCell sx={portalTableHeadCellSx}>Date</TableCell>
                          <TableCell sx={portalTableHeadCellSx}>Status</TableCell>
                          <TableCell sx={portalTableHeadCellSx}>Contribution</TableCell>
                          <TableCell align="right" sx={portalTableHeadCellSx}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((o, idx) => {
                          const fees = Array.isArray(o.fee_lines) ? o.fee_lines : [];
                          return (
                            <TableRow key={o.id} hover sx={portalTableBodyRowSx(idx)}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>{new Date(o.date_created || o.date_created_gmt).toLocaleDateString()}</TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{o.status}</TableCell>
                              <TableCell sx={{ maxWidth: 360 }}>
                                {(Array.isArray(o.line_items) ? o.line_items : []).map((li, liIdx) => (
                                  <Typography key={`${o.id}-stmt-item-${liIdx}`} variant="body2" sx={{ lineHeight: 1.45 }}>
                                    {buildStatementItemLabel(li) || 'Item'}
                                  </Typography>
                                ))}
                                {fees.map((f, fi) => (
                                  <Typography key={`${o.id}-fee-${fi}`} variant="body2" sx={{ lineHeight: 1.45 }}>
                                    {f.name || 'Fee'}
                                  </Typography>
                                ))}
                                {(o.line_items || []).length === 0 && fees.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                ) : null}
                              </TableCell>
                              <TableCell align="right">{fmt(o.total)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ textAlign: 'right', mb: 2 }}>Total: {fmt(total)}</Typography>
                </>
              );
            })()
          ) : null}
        </Box>
      )}
    </>
  );
}
